// File validation utilities

export const FILE_SIZE_LIMITS = {
  AVATAR: 10 * 1024 * 1024, // 10MB for avatars (after compression)
  AVATAR_PRE_COMPRESSION: 50 * 1024 * 1024, // 50MB for avatars before compression
  IMAGE: 50 * 1024 * 1024,  // 50MB for general images
  DOCUMENT: 100 * 1024 * 1024, // 100MB for documents
} as const

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/webp',
  'image/gif'
] as const

export function validateFileSize(file: File, maxSize: number): { isValid: boolean; error?: string } {
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: getFileSizeError(file, maxSize)
    }
  }
  return { isValid: true }
}

export function validateImageFile(file: File): { isValid: boolean; error?: string } {
  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      error: 'No file selected.'
    }
  }

  // Check file type
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as any)) {
    return {
      isValid: false,
      error: `Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.`
    }
  }

  // Check file size (allow larger files since we'll compress them)
  const sizeValidation = validateFileSize(file, FILE_SIZE_LIMITS.AVATAR_PRE_COMPRESSION)
  if (!sizeValidation.isValid) {
    return sizeValidation
  }

  return { isValid: true }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function getFileSizeError(file: File, maxSize: number): string {
  const fileSize = formatFileSize(file.size)
  const maxSizeFormatted = formatFileSize(maxSize)
  return `File size (${fileSize}) exceeds the maximum allowed size of ${maxSizeFormatted}. Please choose a smaller image.`
}
